import { Heading, Stack, useDisclosure, useToast } from '@chakra-ui/react'
import { DocumentReference } from 'firebase/firestore'
import { useState } from 'react'

import FirestoreUser, { diagnosisTitles } from '../../../../types/FirestoreUser'
import { useAdminFileUpload } from '../hooks/useAdminFileUpload'
import { useDeleteDiagnosisDocument } from '../hooks/useDeleteDiagnosisDocument'
import { DeleteDiagnosisDocumentModal } from './DeleteDiagnosisDocumentModal'
import { DiagnosisUploadForm } from './DiagnosisUploadForm'
import { DiagnosisDocumentList } from './DiagnosisDocumentList'
import { DiagnosisVerificationControls } from './DiagnosisVerificationControls'

interface Props {
  profile: FirestoreUser['profile'] | undefined
  user: FirestoreUser
  userRef: DocumentReference<FirestoreUser>
  uid: string
  isSuperAdmin: boolean
  adminName: string
  adminInitials: string
}

export function DiagnosisSection({
  profile,
  user,
  userRef,
  uid,
  isSuperAdmin,
  adminName,
  adminInitials,
}: Props) {
  const toast = useToast()
  const deleteModal = useDisclosure()
  const [deletingDocIndex, setDeletingDocIndex] = useState<number | undefined>()

  const { adminFiles, setAdminFiles, uploading, handleUpload } =
    useAdminFileUpload({
      uid,
      userRef,
      currentDiagnosisDocumentation: profile?.diagnosisDocumentation,
    })

  const { deleteDocument, isDeleting } = useDeleteDiagnosisDocument({
    userRef,
    diagnosisDocumentation: profile?.diagnosisDocumentation,
    existingDeletions: user.diagnosisDocumentDeletions,
    adminName,
    onSuccess: () => {
      deleteModal.onClose()
      setDeletingDocIndex(undefined)
      toast({
        status: 'success',
        title: 'Document deleted successfully',
        isClosable: true,
      })
    },
    onError: (error) => {
      console.error('Failed to delete document:', error)
    },
  })

  function handleOpenDeleteModal(index: number) {
    setDeletingDocIndex(index)
    deleteModal.onOpen()
  }

  async function handleConfirmDelete() {
    if (deletingDocIndex !== undefined) {
      await deleteDocument(deletingDocIndex)
    }
  }

  if (!profile?.diagnosis) return null

  return (
    <Stack spacing={3}>
      <Heading size='md'>
        Diagnosis: {diagnosisTitles[profile.diagnosis]}
      </Heading>

      {isSuperAdmin && (
        <DiagnosisUploadForm
          adminFiles={adminFiles}
          setAdminFiles={setAdminFiles}
          uploading={uploading}
          onUpload={handleUpload}
        />
      )}

      <DiagnosisDocumentList
        profile={profile}
        onDeleteDocument={handleOpenDeleteModal}
      />

      <DiagnosisVerificationControls
        profile={profile}
        userRef={userRef}
        adminInitials={adminInitials}
      />

      <DeleteDiagnosisDocumentModal
        isOpen={deleteModal.isOpen}
        onClose={() => {
          deleteModal.onClose()
          setDeletingDocIndex(undefined)
        }}
        documentNumber={
          deletingDocIndex !== undefined ? deletingDocIndex + 1 : 0
        }
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </Stack>
  )
}
